import { Col, DatePicker, Form, Input, Modal, Row, Select } from "antd";
import dayjs from "dayjs";
import React from "react";
import {
  IResearchProject,
  IUpdateResearchProject,
} from "../../models/Research";
import { useUpdateResearchProject } from "../../query/ReseachQuery";
import { notify } from "../../utils/toast";
import { ProjectStatus } from "../../enum/common";

interface ModalEditProjectProps {
  open: boolean;
  project: IResearchProject | null;
  onClose: () => void;
}

const ModalEditProject = ({
  open,
  project,
  onClose,
}: ModalEditProjectProps) => {
  const [form] = Form.useForm();
  const updateResearchProject = useUpdateResearchProject();

  React.useEffect(() => {
    if (project && open) {
      form.setFieldsValue({
        projectName: project.projectName,
        project: project.project,
        projectDescription: project.projectDescription,
        customerName: project.customerName,
        saleAdInCharge: project.saleAdInCharge,
        saleInCharge: project.saleInCharge,
        purchaseInCharge: project.purchaseInCharge,
        requestFrom: project.requestFrom,
        contactProject: project.contactProject,
        startDate: project.startDate ? dayjs(project.startDate) : null,
        deadline: project.deadline ? dayjs(project.deadline) : null,
        status: project.status,
      });
    }
  }, [project, open, form]);

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (!project) return;
      const formattedValues: IUpdateResearchProject = {
        ...values,
        startDate: values.startDate
          ? values.startDate.format("YYYY-MM-DD")
          : undefined,
        deadline: values.deadline
          ? values.deadline.format("YYYY-MM-DD")
          : undefined,
      };
      await updateResearchProject.mutateAsync(
        { id: project.id, data: formattedValues },
        {
          onSuccess: () => {
            notify.success("Cập nhật dự án thành công");
            form.resetFields();
            onClose();
          },
        },
      );
    } catch (error) {
      notify.error("Cập nhật dự án thất bại");
    }
  };

  return (
    <Modal
      title={`Chỉnh sửa dự án: ${project?.projectName || ""}`}
      open={open}
      onCancel={handleCancel}
      onOk={handleOk}
      okText="Cập nhật"
      cancelText="Hủy"
      confirmLoading={updateResearchProject.isPending}
      width={800}
    >
      <Form layout="vertical" form={form}>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Tên dự án"
              name="projectName"
              rules={[
                {
                  required: true,
                  message: "Vui lòng nhập tên dự án",
                },
              ]}
            >
              <Input placeholder="Nhập tên dự án" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Dự án"
              name="project"
              rules={[
                {
                  required: true,
                  message: "Vui lòng nhập dự án",
                },
              ]}
            >
              <Input placeholder="Nhập dự án" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Mô tả dự án"
              name="projectDescription"
              rules={[
                {
                  required: true,
                  message: "Vui lòng nhập mô tả dự án",
                },
              ]}
            >
              <Input placeholder="Nhập mô tả dự án" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Khách hàng"
              name="customerName"
              rules={[
                {
                  required: true,
                  message: "Vui lòng nhập khách hàng",
                },
              ]}
            >
              <Input placeholder="Nhập khách hàng" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label="Sale Admin phụ trách" name="saleAdInCharge">
              <Input placeholder="Nhập sale admin phụ trách" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Sale phụ trách" name="saleInCharge">
              <Input placeholder="Nhập sale phụ trách" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label="Mua hàng phụ trách" name="purchaseInCharge">
              <Input placeholder="Nhập mua hàng phụ trách" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Yêu cầu từ"
              name="requestFrom"
              rules={[
                {
                  required: true,
                  message: "Vui lòng nhập nơi nhận yêu cầu",
                },
              ]}
            >
              <Input placeholder="Nhập yêu cầu từ" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item
              label="Ngày khởi chạy"
              name="startDate"
              rules={[
                {
                  required: true,
                  message: "Vui lòng chọn ngày khởi chạy",
                },
              ]}
            >
              <DatePicker
                placeholder="Chọn ngày khởi chạy"
                format="DD/MM/YYYY"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              label="Ngày hoàn thành"
              name="deadline"
              rules={[
                {
                  required: true,
                  message: "Vui lòng chọn ngày hoàn thành",
                },
              ]}
            >
              <DatePicker
                placeholder="Chọn ngày hoàn thành"
                format="DD/MM/YYYY"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              label="Trạng thái"
              name="status"
              rules={[
                {
                  required: true,
                  message: "Chọn trạng thái",
                },
              ]}
            >
              <Select placeholder="Chọn trạng thái">
                {Object.values(ProjectStatus).map((status) => (
                  <Select.Option key={status} value={status}>
                    {status}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item
              label="Liên hệ"
              name="contactProject"
              rules={[
                {
                  required: true,
                  message: "Vui lòng nhập liên hệ",
                },
              ]}
            >
              <Input placeholder="Nhập liên hệ" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ModalEditProject;
